import { useState } from 'react'
import { Maximize2 } from 'lucide-react'
import type { Event } from '@/lib/types'
import { Badge } from '@/components/ui'
import ProgressiveImage from '@/components/ProgressiveImage'
import ImageLightbox from '@/components/ImageLightbox'
import { catOf } from '@/lib/types'

/** Instagram portrait aspect ratio (width / height) used for event photos. */
const INSTAGRAM_ASPECT_RATIO = '605 / 750'

/**
 * Event banner: shows the uploaded photo when present, otherwise a zellij
 * lattice tinted with the event's category colour. A category badge overlays
 * the top-left corner. When `zoomable` is set and a photo exists, clicking it
 * opens a full (uncropped) view.
 *
 * Height follows the source's Instagram aspect ratio from the rendered
 * width. Pass `maxHeight` to cap it (the image is then cropped via
 * object-fit: cover).
 */
export default function EventBanner({
  event,
  maxHeight,
  aspectRatio = INSTAGRAM_ASPECT_RATIO,
  rounded = false,
  zoomable = false,
  hideBadge = false,
}: {
  event: Event
  maxHeight?: number
  aspectRatio?: string
  rounded?: boolean
  zoomable?: boolean
  hideBadge?: boolean
}) {
  const cat = catOf(event.type.type)
  const radius = rounded ? 'var(--radius-lg) var(--radius-lg) 0 0' : undefined
  const [open, setOpen] = useState(false)
  const canZoom = zoomable && !!event.image_uri

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio,
        maxHeight,
        background: cat.color,
        overflow: 'hidden',
        borderRadius: radius,
      }}
    >
      {event.image_uri ? (
        <ProgressiveImage
          src={event.image_uri}
          blurHash={event.image_blurhash}
          width="100%"
          height="100%"
          alt={event.title}
        />
      ) : (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: "url('/assets/zellij-tile.svg')",
            backgroundSize: '92px 92px',
            opacity: 0.4,
            mixBlendMode: 'soft-light',
          }}
        />
      )}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to top, rgba(8,30,22,0.5), transparent 55%)',
          pointerEvents: 'none',
        }}
      />
      {!hideBadge && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            display: 'flex',
            gap: 6,
            zIndex: 3,
          }}
        >
          <Badge
            tone={cat.tone}
            solid
            iconLeft={<span>{event.type.emoji}</span>}
          >
            {event.type.type}
          </Badge>
        </div>
      )}

      {canZoom && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setOpen(true)
          }}
          aria-label="Voir l'image en entier"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 4,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 11px',
            borderRadius: 'var(--radius-pill)',
            border: 'none',
            cursor: 'pointer',
            background: 'rgba(8,30,22,0.55)',
            color: 'var(--paper-0)',
            fontFamily: 'var(--font-body)',
            fontSize: 12.5,
            fontWeight: 600,
            backdropFilter: 'blur(4px)',
          }}
        >
          <Maximize2 size={14} /> Voir en entier
        </button>
      )}

      {open && event.image_uri && (
        <ImageLightbox
          src={event.image_uri}
          alt={event.title}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  )
}
